/**
 * parent-analytics-service is an AMD module that returns
 * riqAnalyticsService implementation.
 * Api service.
 *
 * @summary Analytics service for readingIq based on Academy Parent.
 * @version 1.0.0
 * @since 1.0.0
 * @module riq-analytics-service
 */
define([
  'js/aofl-js/0.8.0/aofl-modules/__build/aofljs.min',
  'readingiq-src/js/config/event-type-map'
], function riqAnalyticsServiceImpl(aoflModules, _eventTypeMap) {
  'use strict';

  var storeService = aoflModules.store;
  var analyticsQueue = new aoflModules.AnalyticsQueue();
  var eventTypeMap = _eventTypeMap.typeMap;

  /**
   * @function riqAnalyticsService
   * @param {Object} $window window
   * @param {Object} riqApiService api service
   * @param {Object} riqServerTimeService time service
   * @return {Object} riqAnalyticsService
   **/
  var riqAnalyticsService = function riqAnalyticsService(
    $window,
    riqApiService,
    riqServerTimeService
  ) {
    // @to-do move shim to a seperate file
    window.requestIdleCallback =
      window.requestIdleCallback ||
      function(cb) {
        var start = Date.now();
        return setTimeout(function() {
          cb({
            didTimeout: false,
            timeRemaining: function() {
              return Math.max(0, 50 - (Date.now() - start));
            }
          });
        }, 1);
      };

    window.cancelIdleCallback =
      window.cancelIdleCallback ||
      function(id) {
        clearTimeout(id);
      };

    var interval = null;

    var sharedContext = {
      device_id: null,
      log_type: 'web_client',
      user: {
        master_uuid: null,
        profile_uuid: null,
        user_uuid: null,
        user_description: null
      }
    };

    var v1Context = {
      app_session_id: null
    };

    var v2Context = {
      device_info: {
        form_factor: 'web'
      },
      domain: window.location.hostname,
      log_source: 'web_client',
      log_version: '2.0'
    };

    // the following event should only be logged as v2 events
    var noLegacyEvents = [
      'com.aofl.readingiq.activity.open',
      'com.aofl.readingiq.activity.close',
      'com.aofl.readingiq.activity.session.start',
      'com.aofl.readingiq.activity.session.update',
      'com.aofl.readingiq.activity.session.end',
      'com.aofl.readingiq.profile.create',
      'com.aofl.readingiq.profile.update',
      'com.aofl.readingiq.profile.swap'
    ];

    storeService.subscribe(function storeSubscribe() {
      var state = storeService.getState();
      var roles = state.profile?.role || [];
      sharedContext.device_id = state.appSession?.deviceId || null;
      sharedContext.user.user_uuid = state.user?.userUuid || null;
      sharedContext.user.master_uuid = state.user?.masterUserUuid || null;
      sharedContext.user.profile_uuid = state.profile?.profileUuid || null;
      sharedContext.user.user_description =
        state.user?.userDescription || roles[0]?.type || null;
      v1Context.app_session_id = state.appSession?.appSessionId || null;
      v2Context.device_info =
        state.appSession && state.appSession.userAgent
          ? Object.assign(v2Context.device_info, {
              user_agent: state.appSession.userAgent,
              operating_system: state.appSession.operatingSystem,
              app_version: state.appSession.appVersion,
              graphics_type: state.appSession.graphicsType,
              graphics_version: state.appSession.graphicsVersion,
              device_pixel_ratio: state.appSession.devicePixelRatio,
              long_side_logical_pixels: state.appSession.longSideLogicalPixels,
              short_side_logical_pixels: state.appSession.shortSideLogicalPixels
            })
          : v2Context.device_info;
    });

    var filterOldEvents = function filterOldEvents(evt) {
      var isOldEvent = evt.context.log_version !== '2.0';
      var eventType = evt.event_type || evt.eventType;
      var eventIsExcluded = noLegacyEvents.includes(eventType);
      return isOldEvent && !eventIsExcluded;
    };

    var filterNewEvents = function filterNewEvents(evt) {
      return evt.context.log_version === '2.0';
    };

    var enqueueEvent = function enqueueEvent(eventObject, immediate) {
      var eventType = eventObject.event_type || eventObject.eventType;
      var eventSubtype = eventObject.payload.type;
      var newEventType = eventTypeMap[eventType] || eventType;

      if (eventSubtype && typeof newEventType === 'object') {
        newEventType = newEventType[eventSubtype];
      }

      var oldEventContext = Object.assign({}, sharedContext, v1Context);
      var newEventContext = Object.assign({}, sharedContext, v2Context);

      eventObject.context = oldEventContext;
      eventObject.timestamp = riqServerTimeService.getAdjustedTime();

      var newEventObject = Object.assign({}, eventObject, {
        event_type: newEventType,
        context: newEventContext
      });

      if (immediate) {
        if (filterOldEvents(eventObject)) {
          riqApiService.post('logEvent', {events: [eventObject]});
        }
        riqApiService.post('sendEvent', {events: [newEventObject]});
      } else {
        analyticsQueue.enqueueEvent(eventObject);
        analyticsQueue.enqueueEvent(newEventObject);
      }
    };

    var sendQueuedEvents = function sendQueuedEvents(queue) {
      if (queue.length) {
        var oldEvents = queue.filter(filterOldEvents);
        var newEvents = queue.filter(filterNewEvents);
        if (oldEvents.length) {
          riqApiService.post('logEvent', {events: oldEvents});
        }
        if (newEvents.length) {
          riqApiService.post('sendEvent', {events: newEvents});
        }
      }
      return Promise.resolve();
    };

    var resetInterval = function resetInterval() {
      var sendInterval = aofljsConfig.applicationDebug ? 1000 : 10000;
      $window.clearInterval(interval);
      interval = $window.setInterval(function() {
        requestIdleCallback(function analyticsQueueInterval() {
          analyticsQueue.sendQueue(sendQueuedEvents);
        });
      }, sendInterval);
    };

    return {
      enqueueEvent: enqueueEvent,
      resetInterval: resetInterval,
      getAnalyticsQueue: function() {
        return analyticsQueue;
      }
    };
  };

  return riqAnalyticsService;
});
